import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Database,
  HardDrive,
  RefreshCw,
  Server,
  ShieldCheck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { api } from './api';

export type BackupComponentKind = 'backup' | 'maintenance' | 'deepVerify';
export type BackupComponentStatus = 'OK' | 'FAILED' | 'STALE' | 'MISSING' | 'PENDING';
export type BackupOverallStatus = 'OK' | 'WARNING' | 'CRITICAL' | 'NOT_CONFIGURED';

export type BackupHealthComponent = {
  kind: BackupComponentKind;
  label: string;
  status: BackupComponentStatus;
  timestampUtc: string | null;
  snapshotId: string;
  message: string;
  components: string[];
  ageSeconds: number | null;
  staleAfterSeconds: number;
};

export type BackupHealthReport = {
  configured: boolean;
  overall: BackupOverallStatus;
  alertRequired: boolean;
  evaluatedAt: string;
  components: Record<BackupComponentKind, BackupHealthComponent>;
  schedule: { backup: string; maintenance: string; deepVerify: string };
  retention: {
    withinHours: number;
    daily: number;
    weekly: number;
    monthly: number;
    yearly: number;
    repositoryReadPercent: number;
  };
};

export const BACKUP_HEALTH_REFRESH_MS = 30_000;

const OVERALL_PRESENTATION: Record<BackupOverallStatus, {
  tone: string;
  eyebrow: string;
  title: string;
  text: string;
  icon: ReactNode;
}> = {
  OK: {
    tone: 'ok',
    eyebrow: 'ЗАЩИТА ДАННЫХ РАБОТАЕТ',
    title: 'Резервные копии защищены',
    text: 'Последняя копия свежая, обязательные проверки проходят штатно.',
    icon: <ShieldCheck />,
  },
  WARNING: {
    tone: 'warning',
    eyebrow: 'НУЖНА ПРОВЕРКА',
    title: 'Требуется внимание',
    text: 'Основная копия доступна, но одна из периодических проверок просрочена или завершилась ошибкой.',
    icon: <Clock3 />,
  },
  CRITICAL: {
    tone: 'critical',
    eyebrow: 'КРИТИЧЕСКИЙ РИСК',
    title: 'Резервные копии под угрозой',
    text: 'Последняя копия или глубокая проверка восстановления не подтверждена. Администраторы уже уведомлены.',
    icon: <AlertTriangle />,
  },
  NOT_CONFIGURED: {
    tone: 'neutral',
    eyebrow: 'НЕТ ДАННЫХ СЕРВЕРА',
    title: 'Мониторинг не подключён',
    text: 'Каталог безопасных статусов не примонтирован к API. Резервные копии могут продолжать работать отдельно.',
    icon: <Server />,
  },
};

const COMPONENT_PRESENTATION: Record<BackupComponentStatus, { label: string; tone: string }> = {
  OK: { label: 'Успешно', tone: 'ok' },
  FAILED: { label: 'Ошибка', tone: 'critical' },
  STALE: { label: 'Просрочено', tone: 'warning' },
  MISSING: { label: 'Нет данных', tone: 'critical' },
  PENDING: { label: 'Ожидает первого запуска', tone: 'pending' },
};

const COMPONENT_ICONS: Record<BackupComponentKind, ReactNode> = {
  backup: <HardDrive />,
  maintenance: <Database />,
  deepVerify: <ShieldCheck />,
};

const COMPONENT_NAMES: Record<string, string> = {
  POSTGRES: 'PostgreSQL',
  REDIS: 'Redis',
  MINIO: 'MinIO',
  FILES: 'Файлы',
  RESTIC: 'Restic',
  RESTORE: 'Восстановление',
};

function formatDateTime(value: string | null) {
  if (!value) return 'Ещё не выполнялась';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Время не определено';
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatAge(seconds: number | null) {
  if (seconds === null) return 'нет предыдущего запуска';
  if (seconds < 60) return 'только что';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин назад`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} ч назад`;
  return `${Math.floor(seconds / 86400)} дн назад`;
}

function BackupComponentCard({ item }: { item: BackupHealthComponent }) {
  const state = COMPONENT_PRESENTATION[item.status];
  return <article className={`backup-check-card backup-tone-${state.tone}`}>
    <header>
      <span className="backup-check-icon">{COMPONENT_ICONS[item.kind]}</span>
      <span className="backup-status-pill"><i />{state.label}</span>
    </header>
    <div className="backup-check-title">
      <h3>{item.label}</h3>
      <small>{formatAge(item.ageSeconds)}</small>
    </div>
    <p>{item.message}</p>
    <dl>
      <div><dt>Последний запуск</dt><dd>{formatDateTime(item.timestampUtc)}</dd></div>
      {item.snapshotId && <div><dt>Точка Restic</dt><dd><code>{item.snapshotId}</code></dd></div>}
      {item.components.length > 0 && <div><dt>Проверено</dt><dd>{item.components.map(value => COMPONENT_NAMES[value] || value).join(' · ')}</dd></div>}
    </dl>
  </article>;
}

export function BackupHealthLoadError({ message }: { message: string }) {
  return <section className="surface backup-health-error" role="alert">
    <span><AlertTriangle /></span>
    <div><h2>Не удалось получить состояние резервных копий</h2><p>{message}</p></div>
  </section>;
}

export function BackupHealthView({
  data,
  onRefresh,
  isRefreshing,
}: {
  data: BackupHealthReport;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  const overall = OVERALL_PRESENTATION[data.overall];
  return <div className="admin-section-stack backup-health-panel" aria-live="polite">
    <section className={`backup-health-hero backup-tone-${overall.tone}`}>
      <div className="backup-health-hero-icon">{overall.icon}</div>
      <div className="backup-health-hero-copy">
        <small>{overall.eyebrow}</small>
        <h2>{overall.title}</h2>
        <p>{overall.text}</p>
      </div>
      <div className="backup-health-hero-actions">
        <span>Проверено {formatDateTime(data.evaluatedAt)}</span>
        <button className="button ghost" type="button" onClick={onRefresh} disabled={isRefreshing}>
          <RefreshCw className={isRefreshing ? 'spinning' : ''} />
          {isRefreshing ? 'Обновляем…' : 'Обновить состояние'}
        </button>
      </div>
    </section>

    <div className="backup-check-grid">
      <BackupComponentCard item={data.components.backup} />
      <BackupComponentCard item={data.components.maintenance} />
      <BackupComponentCard item={data.components.deepVerify} />
    </div>

    <div className="backup-policy-grid">
      <section className="surface backup-schedule-card">
        <header><span><CalendarClock /></span><div><h3>Расписание</h3><p>Задания выполняются автоматически по времени сервера</p></div></header>
        <ul>
          <li><i>01</i><div><b>Резервная копия</b><small>{data.schedule.backup}</small></div></li>
          <li><i>02</i><div><b>Обслуживание Restic</b><small>{data.schedule.maintenance}</small></div></li>
          <li><i>03</i><div><b>Контрольное восстановление</b><small>{data.schedule.deepVerify}</small></div></li>
        </ul>
      </section>

      <section className="surface backup-retention-card">
        <header><span><Database /></span><div><h3>Политика хранения</h3><p>Зашифрованная история точек на NAS</p></div></header>
        <div className="backup-retention-cloud">
          <strong>{data.retention.withinHours} ч</strong><span>все точки</span>
        </div>
        <div className="backup-retention-list">
          <span><b>{data.retention.daily}</b> ежедневных</span>
          <span><b>{data.retention.weekly}</b> еженедельных</span>
          <span><b>{data.retention.monthly}</b> ежемесячных</span>
          <span><b>{data.retention.yearly}</b> ежегодных</span>
        </div>
        <footer><CheckCircle2 /> Каждую неделю читается и проверяется {data.retention.repositoryReadPercent}% данных репозитория</footer>
      </section>
    </div>
  </div>;
}

export function BackupAdminPanel() {
  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['system', 'backup-health'],
    queryFn: () => api<BackupHealthReport>('/system/backup-health'),
    refetchInterval: BACKUP_HEALTH_REFRESH_MS,
  });

  if (isLoading) return <section className="surface backup-health-loading"><RefreshCw className="spinning" /><p>Получаем состояние резервных копий…</p></section>;
  if (!data) return <BackupHealthLoadError message={error instanceof Error ? error.message : 'Сервер не вернул данные мониторинга'} />;
  return <BackupHealthView data={data} onRefresh={() => { void refetch(); }} isRefreshing={isFetching} />;
}
